import type { KeyboardEvent, ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";

export type WorkbenchJourneyItemState = "complete" | "current" | "upcoming";

export interface WorkbenchJourneyItem {
  id: string;
  label: ReactNode;
  eyebrow?: ReactNode;
  state?: WorkbenchJourneyItemState;
  disabled?: boolean;
}

export interface WorkbenchJourneyNavigationProps {
  items: WorkbenchJourneyItem[];
  onItemSelect?: (item: WorkbenchJourneyItem) => void;
  ariaLabel?: string;
  className?: string;
}

const activateItem = (
  event: KeyboardEvent<HTMLButtonElement>,
  item: WorkbenchJourneyItem,
  onItemSelect?: (item: WorkbenchJourneyItem) => void,
) => {
  if (!onItemSelect || item.disabled || (event.key !== "Enter" && event.key !== " "))
    return;
  event.preventDefault();
  onItemSelect(item);
};

export function WorkbenchJourneyNavigation({
  items,
  onItemSelect,
  ariaLabel = "Journey",
  className,
}: WorkbenchJourneyNavigationProps) {
  return (
    <nav
      aria-label={ariaLabel}
      data-monkeys-component="workbench-journey-navigation"
      className={cn(
        "min-w-0 overflow-x-auto rounded-lg border border-border/70 bg-card p-2",
        className,
      )}
    >
      <ol className="flex min-w-max items-stretch gap-1">
        {items.map((item, index) => {
          const state = item.state || "upcoming";
          return (
            <li key={item.id} className="flex items-center">
              {index ? (
                <span className="mx-1 h-px w-5 bg-border" aria-hidden="true" />
              ) : null}
              <button
                type="button"
                disabled={item.disabled}
                aria-current={state === "current" ? "page" : undefined}
                onClick={
                  onItemSelect && !item.disabled
                    ? () => onItemSelect(item)
                    : undefined
                }
                onKeyDown={(event) => activateItem(event, item, onItemSelect)}
                className={cn(
                  "min-w-28 rounded-md border px-3 py-2 text-left outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none",
                  state === "current" &&
                    "border-primary bg-primary text-primary-foreground",
                  state === "complete" &&
                    "border-primary/30 bg-primary/10 text-foreground",
                  state === "upcoming" &&
                    "border-transparent bg-muted/50 text-muted-foreground",
                  item.disabled && "cursor-not-allowed opacity-50",
                )}
              >
                {hasRenderableNode(item.eyebrow) ? (
                  <span
                    className={cn(
                      "block text-[0.625rem] font-semibold uppercase tracking-[0.14em]",
                      state === "current"
                        ? "text-primary-foreground/75"
                        : "text-muted-foreground",
                    )}
                  >
                    {item.eyebrow}
                  </span>
                ) : null}
                <span className="mt-0.5 block text-xs font-semibold">
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
